import React, { useState, useEffect, useMemo } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { Table, ColumnDef, EditableOption } from "@/components/NewTable";
import { shiftsService } from "@/services/shiftService";
import { ShiftType } from "@/types/db/shifts";
import { theme } from "@/constants/themes";

const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral" | "todo">> = {
  PENDING: "pending",
  APPROVED: "complete",
  ADVANCED: "complete",
  CANCELED: "attention",
  "CANCELED - BILLABLE": "neutral",
  COMPLETE: "todo",
};

export interface CancelProjectModalProps {
  projectId: string;
  projectName: string;
  targetProjectStatus: "CANCELED" | "LOST" | "ON HOLD";
  onConfirm: (shiftUpdates: { shiftId: string; targetStatus: string }[]) => void;
  onCancel: () => void;
}

const TARGET_STATUS_CONFIGS: Record<string, { defaultShiftStatus: string; nonActionable: string[] }> = {
  CANCELED: { defaultShiftStatus: "CANCELED", nonActionable: ["CANCELED", "CANCELED - BILLABLE", "COMPLETE"] },
  LOST: { defaultShiftStatus: "CANCELED", nonActionable: ["CANCELED", "CANCELED - BILLABLE", "COMPLETE"] },
  "ON HOLD": {
    defaultShiftStatus: "PENDING",
    nonActionable: ["PENDING", "CANCELED", "CANCELED - BILLABLE", "COMPLETE"],
  },
};

export function CancelProjectModal({
  projectId,
  projectName,
  targetProjectStatus,
  onConfirm,
  onCancel,
}: CancelProjectModalProps) {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const projectLabel = displayNames?.projects?.name || "Project";
  const shiftLabel = displayNames?.shifts?.name || "Shift";
  const shiftLabelPlural = displayNames?.shifts?.plural || "Shifts";
  const [loading, setLoading] = useState(true);
  const [actionableShifts, setActionableShifts] = useState<ShiftType[]>([]);
  const [targetStatuses, setTargetStatuses] = useState<Record<string, string>>({});

  const config = TARGET_STATUS_CONFIGS[targetProjectStatus] || TARGET_STATUS_CONFIGS.CANCELED;

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const allShifts = await shiftsService.getShiftsByProjectId(projectId);
        const filtered = allShifts.filter((s) => s.status && !config.nonActionable.includes(s.status));
        setActionableShifts(filtered);
        const initial: Record<string, string> = {};
        for (const s of filtered) {
          if (s.id) initial[s.id] = config.defaultShiftStatus;
        }
        setTargetStatuses(initial);
      } catch (err) {
        console.error("Failed to fetch shifts for cancel modal:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchShifts();
  }, [projectId, config]);

  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status || "PENDING"] || "pending";
    return colors[colorKey].border;
  };

  const targetStatusOptions: EditableOption[] = useMemo(() => {
    if (targetProjectStatus === "ON HOLD") {
      return [
        { value: "PENDING", label: "Pending", color: colors.pending.border },
        { value: "CANCELED", label: "Canceled", color: colors.attention.border },
        { value: "CANCELED - BILLABLE", label: "Canceled - Billable", color: colors.neutral.border },
      ];
    }
    return [
      { value: "CANCELED", label: "Canceled", color: colors.attention.border },
      { value: "CANCELED - BILLABLE", label: "Canceled - Billable", color: colors.neutral.border },
    ];
  }, [colors, targetProjectStatus]);

  const columns: ColumnDef<ShiftType>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        flex: 1,
        accessor: (row) => row.name || row.description || `Unnamed ${shiftLabel}`,
      },
      {
        key: "current_status",
        header: "Current Status",
        width: 170,
        align: "center",
        render: (_value, row) => (
          <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
            <ThemedText style={styles.statusText}>{row.status || "PENDING"}</ThemedText>
          </View>
        ),
      },
      {
        key: "status",
        header: "Target Status",
        width: 210,
        align: "center",
        editable: {
          type: "picker",
          options: targetStatusOptions,
          placeholder: "Select status",
          onEdit: (row: ShiftType, _key: string, newValue: string) => {
            if (row.id) {
              setTargetStatuses((prev) => ({ ...prev, [row.id!]: newValue }));
            }
          },
        },
        accessor: (row) => (row.id ? targetStatuses[row.id] || config.defaultShiftStatus : config.defaultShiftStatus),
        render: (_value, row) => {
          const target = row.id ? targetStatuses[row.id] || config.defaultShiftStatus : config.defaultShiftStatus;
          const option = targetStatusOptions.find((o) => o.value === target);
          return (
            <View style={[styles.statusPill, { backgroundColor: option?.color || colors.attention.border }]}>
              <ThemedText style={styles.statusText}>{target}</ThemedText>
            </View>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetStatuses, targetStatusOptions, colors],
  );

  const handleConfirm = () => {
    const shiftUpdates = actionableShifts
      .filter((s) => s.id)
      .map((s) => ({
        shiftId: s.id!,
        targetStatus: targetStatuses[s.id!] || config.defaultShiftStatus,
      }));
    onConfirm(shiftUpdates);
  };

  const headerLabels: Record<string, string> = {
    CANCELED: `Cancel ${projectLabel}`,
    LOST: `Lost ${projectLabel}`,
    "ON HOLD": `${projectLabel} On Hold`,
  };
  const headerLabel = headerLabels[targetProjectStatus] || `${targetProjectStatus} ${projectLabel}`;
  const hasActionableShifts = actionableShifts.length > 0;

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {/* Header */}
      <View
        style={[styles.header, { backgroundColor: colors.attention.background, borderColor: colors.attention.border }]}
      >
        <MaterialCommunityIcons name="alert-circle" size={24} color={colors.attention.text} />
        <ThemedText type="subtitle" style={{ color: colors.attention.text, marginLeft: 8, flex: 1 }}>
          {headerLabel} — Update {shiftLabelPlural}
        </ThemedText>
      </View>

      {/* Body */}
      <View style={styles.body}>
        <ThemedText style={styles.message}>
          You&apos;re about to change <ThemedText style={styles.bold}>{projectName}</ThemedText> to{" "}
          <ThemedText style={styles.bold}>{targetProjectStatus}</ThemedText>.
        </ThemedText>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={colors.primary} />
            <ThemedText style={{ marginLeft: 8, color: colors.textSecondary }}>
              Loading {shiftLabelPlural.toLowerCase()}...
            </ThemedText>
          </View>
        ) : hasActionableShifts ? (
          <>
            <ThemedText style={styles.subMessage}>
              The following active {shiftLabelPlural.toLowerCase()} can be updated. Choose a target status for each:
            </ThemedText>
            <View style={styles.tableContainer}>
              <Table<ShiftType>
                data={actionableShifts}
                columns={columns}
                keyExtractor={(item) => item.id || ""}
                emptyMessage={`No ${shiftLabelPlural.toLowerCase()}`}
              />
            </View>
          </>
        ) : (
          <View
            style={[
              styles.infoBox,
              { backgroundColor: colors.complete.background, borderColor: colors.complete.border },
            ]}
          >
            <MaterialCommunityIcons name="check-circle" size={20} color={colors.complete.text} />
            <ThemedText style={[styles.infoText, { color: colors.complete.text }]}>
              All {shiftLabelPlural.toLowerCase()} are already completed or canceled.
            </ThemedText>
          </View>
        )}
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text={
            hasActionableShifts
              ? `Update ${actionableShifts.length} ${actionableShifts.length !== 1 ? shiftLabelPlural : shiftLabel} & Save`
              : "Save"
          }
          icon="check"
          onPress={handleConfirm}
          disabled={loading}
          style={[styles.confirmButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          iconStyle={{ color: "white" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 560,
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  body: {
    padding: 16,
    gap: 12,
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
  },
  bold: {
    fontWeight: "700",
  },
  subMessage: {
    fontSize: 13,
    opacity: 0.7,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  tableContainer: {
    maxHeight: 300,
    overflow: "hidden",
    borderRadius: 8,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 10,
  },
  infoText: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    padding: 12,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 36,
    paddingHorizontal: 16,
  },
});
